import React, { useEffect, useState } from 'react';
import { motion, useSpring, AnimatePresence } from 'framer-motion';
import { Database, Sparkles } from 'lucide-react';
import { getUserStorage, PersonalStorage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface PersonalStorageCardProps {
  /**
   * Slim landing-surface variant — no icon, no breakdown chips, no hover-scale,
   * no shimmer. Two-row card (label + numbers, slim progress bar) at ~half the
   * height of the full card. Traffic-light quota colors and the >95% pulse are
   * preserved because they carry real warning signal. The first-time
   * "unlocked permanent storage" tooltip is skipped here so it still fires on
   * /my-files where the full card lives.
   */
  compact?: boolean;
}

const PersonalStorageCard: React.FC<PersonalStorageCardProps> = ({ compact = false }) => {
  const { getValidToken } = useAuth();
  const [storageInfo, setStorageInfo] = useState<PersonalStorage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Animated count-up for storage GB
  const storageSpring = useSpring(0, { stiffness: 100, damping: 20 });

  useEffect(() => {
    const fetchStorageInfo = async () => {
      try {
        const token = await getValidToken();
        if (!token) {
          setError('Authentication required');
          setIsLoading(false);
          return;
        }

        const info = await getUserStorage(token);
        setStorageInfo(info);
        setError(null);

        // Animate count-up
        const newStorageGB = info.storage_bytes / (1024 * 1024 * 1024);
        storageSpring.set(newStorageGB);
      } catch (err) {
        console.error('Failed to fetch personal storage info:', err);
        setError('Failed to load storage information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStorageInfo();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStorageInfo, 30000);
    return () => clearInterval(interval);
  }, [getValidToken, storageSpring]);

  // First-time experience tooltip — skipped in compact so the slim landing
  // variant stays quiet and the celebration still fires on /my-files
  // (which renders the full card and its own first-view counter).
  useEffect(() => {
    if (compact) return;
    const viewCount = parseInt(localStorage.getItem('storage_card_views') || '0');
    if (viewCount < 1 && !isLoading && storageInfo) {
      setShowTooltip(true);
      localStorage.setItem('storage_card_views', String(viewCount + 1));

      // Auto-hide tooltip after 5 seconds
      const timeout = setTimeout(() => setShowTooltip(false), 5000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, storageInfo, compact]);

  if (isLoading) {
    if (compact) {
      return (
        <div
          className="rounded-xl backdrop-blur-md border shadow-sm px-4 py-3 animate-pulse"
          style={{
            background:
              'linear-gradient(135deg, rgba(23, 23, 23, 0.55) 0%, rgba(38, 38, 38, 0.5) 100%)',
            borderColor: 'rgba(255, 255, 255, 0.06)',
          }}
        >
          <div className="h-3 bg-neutral-800/50 rounded mb-2 w-40" />
          <div className="h-1.5 bg-neutral-800/50 rounded" />
        </div>
      );
    }
    return (
      <div className="relative rounded-2xl overflow-hidden backdrop-blur-md bg-gradient-to-r from-neutral-900/60 via-neutral-800/60 to-neutral-900/60 border border-neutral-700/50 p-6 shadow-lg animate-pulse">
        <div className="h-16 bg-neutral-800/50 rounded-lg"></div>
      </div>
    );
  }

  if (error || !storageInfo) {
    return null; // Silently hide if there's an error
  }

  const storageGB = storageInfo.storage_bytes / (1024 * 1024 * 1024);
  const maxGB = storageInfo.storage_max / (1024 * 1024 * 1024);
  const usagePercentage = (storageGB / maxGB) * 100;

  // Audio / Original / Video breakdown — backend may not yet return these; default to 0 (treat as audio-only).
  const audioBytes = storageInfo.audio_storage_bytes ?? storageInfo.storage_bytes;
  const videoBytes = storageInfo.video_storage_bytes ?? 0;
  const videoCount = storageInfo.video_count ?? 0;
  const originalBytesRaw = storageInfo.original_storage_bytes ?? 0;
  const originalCount = storageInfo.original_count ?? 0;
  const hasVideos = videoBytes > 0 && videoCount > 0;
  const hasOriginals = originalBytesRaw > 0 && originalCount > 0;
  const audioGB = audioBytes / (1024 * 1024 * 1024);
  const originalGB = originalBytesRaw / (1024 * 1024 * 1024);
  const videoGB = videoBytes / (1024 * 1024 * 1024);
  const audioPercent = Math.min((audioGB / maxGB) * 100, 100);
  const originalPercent = Math.min((originalGB / maxGB) * 100, Math.max(0, 100 - audioPercent));
  const videoPercent = Math.min((videoGB / maxGB) * 100, Math.max(0, 100 - audioPercent - originalPercent));

  const getProgressColor = () => {
    if (usagePercentage < 80) return 'from-emerald-500 to-green-400';
    if (usagePercentage < 95) return 'from-yellow-500 to-orange-400';
    return 'from-red-500 to-pink-500';
  };

  const getNumberColor = () => {
    if (usagePercentage < 80) return 'text-emerald-400';
    if (usagePercentage < 95) return 'text-yellow-400';
    return 'text-red-400';
  };

  const shouldPulse = usagePercentage > 95;

  // Slim landing variant — half the height, no icon, no breakdown chips, no
  // hover-scale, no shimmer. Traffic-light quota color and the >95% pulse are
  // kept because they carry actual warning signal. Order of the labelled
  // numbers matches the full card (used GB / max GB · percent) so the muscle
  // memory carries between /my-files (full) and the landing (compact).
  if (compact) {
    return (
      <motion.div
        className="rounded-xl overflow-hidden backdrop-blur-md border shadow-sm"
        style={{
          background:
            'linear-gradient(135deg, rgba(23, 23, 23, 0.55) 0%, rgba(38, 38, 38, 0.5) 100%)',
          borderColor: 'rgba(255, 255, 255, 0.06)',
        }}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="px-4 py-3">
          <div className="flex items-baseline justify-between mb-2 gap-3">
            <span
              className="text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap"
              style={{ color: 'rgba(255, 255, 255, 0.5)' }}
            >
              Personal storage
            </span>
            <span
              className="text-xs text-right whitespace-nowrap"
              style={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              <span className={`font-semibold ${getNumberColor()}`}>
                {storageGB.toFixed(2)} GB
              </span>
              <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                {' '}/ {maxGB.toFixed(0)} GB · {usagePercentage.toFixed(0)}%
              </span>
            </span>
          </div>
          <div className="relative h-1.5 rounded-full overflow-hidden bg-neutral-800/60">
            <motion.div
              className={`h-full bg-gradient-to-r ${getProgressColor()}`}
              initial={{ width: 0 }}
              animate={{
                width: `${Math.min(usagePercentage, 100)}%`,
                scale: shouldPulse ? [1, 1.02, 1] : 1,
              }}
              transition={{
                width: { duration: 1.2, ease: [0.34, 1.56, 0.64, 1] },
                scale: shouldPulse
                  ? { duration: 2, repeat: Infinity }
                  : { duration: 0 },
              }}
            />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="relative">
      {/* Main Card */}
      <motion.div
        className="relative rounded-2xl overflow-hidden backdrop-blur-md border shadow-lg"
        style={{
          background: 'linear-gradient(135deg, rgba(23, 23, 23, 0.7) 0%, rgba(38, 38, 38, 0.6) 50%, rgba(23, 23, 23, 0.7) 100%)',
          borderColor: 'rgba(255, 72, 0, 0.2)',
          boxShadow: isHovered
            ? '0 20px 40px -12px rgba(255, 72, 0, 0.3), 0 8px 16px -8px rgba(0, 0, 0, 0.4)'
            : '0 10px 30px -8px rgba(255, 72, 0, 0.15), 0 4px 12px -4px rgba(0, 0, 0, 0.3)'
        }}
        initial={{ opacity: 0, y: -20 }}
        animate={{
          opacity: 1,
          y: 0,
          scale: isHovered ? 1.01 : 1
        }}
        transition={{
          duration: 0.6,
          type: 'spring',
          stiffness: 200,
          damping: 20
        }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        {/* Animated gradient background */}
        <motion.div
          className="absolute inset-0 opacity-30"
          style={{
            background: 'linear-gradient(120deg, transparent, rgba(255, 72, 0, 0.1), transparent)',
            backgroundSize: '200% 100%'
          }}
          animate={{
            backgroundPosition: ['0% 0%', '200% 0%']
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear'
          }}
        />

        {/* Content */}
        <div className="relative px-6 py-5">
          <div className="flex items-center gap-6">
            {/* Animated Icon */}
            <motion.div
              className="relative w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #ff4800 0%, #ff7a00 100%)',
                boxShadow: '0 4px 12px rgba(255, 72, 0, 0.4)'
              }}
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: 'spring', stiffness: 300, damping: 15 }}
            >
              {/* Rotating gradient overlay */}
              <motion.div
                className="absolute inset-0"
                style={{
                  background: 'conic-gradient(from 0deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
              />
              <Database className="w-6 h-6 text-white relative z-10" strokeWidth={2.5} />
            </motion.div>

            {/* Storage Information */}
            <div className="flex-1 min-w-0">
              {/* Label */}
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                >
                  Personal Storage
                </span>
              </div>

              {/* Storage Numbers */}
              <div className="flex items-baseline gap-2 mb-1">
                <motion.span
                  className={`text-2xl font-bold ${getNumberColor()}`}
                  key={storageGB}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                >
                  {storageGB.toFixed(2)} GB
                </motion.span>
                <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  / {maxGB.toFixed(0)} GB
                </span>
                <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                  • {usagePercentage.toFixed(0)}% used
                </span>
              </div>

              {/* Audio / Original / Video breakdown sublabel — render whenever any non-audio bucket is non-zero.
                  Chips compose in fixed reading order (audio → original → video) regardless of which are present,
                  so the visual grammar matches the stacked progress bar below. */}
              {(hasOriginals || hasVideos) ? (
                <div
                  className="flex items-center gap-2 mb-2 text-[11px] flex-wrap"
                  style={{ color: 'rgba(255, 255, 255, 0.45)' }}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="inline-block w-2 h-2 rounded-sm"
                      style={{ background: 'linear-gradient(90deg, rgba(52, 211, 153, 0.9), rgba(74, 222, 128, 0.9))' }}
                    />
                    {audioGB.toFixed(2)} GB audio
                  </span>
                  {hasOriginals && (
                    <>
                      <span className="opacity-50">+</span>
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="inline-block w-2 h-2 rounded-sm"
                          style={{ background: 'linear-gradient(90deg, rgba(251, 191, 36, 0.9), rgba(245, 158, 11, 0.9))' }}
                        />
                        {originalGB.toFixed(2)} GB original
                      </span>
                    </>
                  )}
                  {hasVideos && (
                    <>
                      <span className="opacity-50">+</span>
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="inline-block w-2 h-2 rounded-sm"
                          style={{ background: 'rgba(255, 122, 0, 0.55)' }}
                        />
                        {videoGB.toFixed(2)} GB video
                      </span>
                    </>
                  )}
                </div>
              ) : (
                <div className="mb-2" />
              )}

              {/* Liquid Progress Bar — stacked audio + video segments */}
              <div className="relative h-2.5 rounded-full overflow-hidden bg-neutral-800/60 backdrop-blur-sm flex">
                {/* Audio segment — retains traffic-light quota gradient (load-bearing fill signal) */}
                <motion.div
                  className={`h-full bg-gradient-to-r ${getProgressColor()} relative`}
                  initial={{ width: 0 }}
                  animate={{
                    width: `${audioPercent}%`,
                    scale: shouldPulse ? [1, 1.02, 1] : 1
                  }}
                  transition={{
                    width: { duration: 1.2, ease: [0.34, 1.56, 0.64, 1] },
                    scale: shouldPulse ? { duration: 2, repeat: Infinity } : { duration: 0 }
                  }}
                  style={{
                    boxShadow: `0 0 ${shouldPulse ? '12px' : '8px'} ${
                      usagePercentage > 95 ? 'rgba(239, 68, 68, 0.4)' :
                      usagePercentage > 80 ? 'rgba(251, 191, 36, 0.4)' :
                      'rgba(52, 211, 153, 0.4)'
                    }`
                  }}
                >
                  {/* Shimmer effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '200%' }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'linear'
                    }}
                  />
                </motion.div>

                {/* Original segment — amber/gold to match the WAV/lossless tile on the artifacts shelf;
                    yellow = "the kept original", visually distinct from emerald (audio) and orange (video). */}
                {hasOriginals && (
                  <motion.div
                    className="h-full relative"
                    initial={{ width: 0 }}
                    animate={{ width: `${originalPercent}%` }}
                    transition={{ duration: 1.2, ease: [0.34, 1.56, 0.64, 1], delay: 0.1 }}
                    style={{
                      background: 'linear-gradient(90deg, rgba(251, 191, 36, 0.85), rgba(245, 158, 11, 0.95))',
                      boxShadow: '0 0 6px rgba(245, 158, 11, 0.3)'
                    }}
                  />
                )}

                {/* Video segment — subordinate soundwave-tinted occupancy, only when present */}
                {hasVideos && (
                  <motion.div
                    className="h-full relative"
                    initial={{ width: 0 }}
                    animate={{ width: `${videoPercent}%` }}
                    transition={{ duration: 1.2, ease: [0.34, 1.56, 0.64, 1], delay: 0.2 }}
                    style={{
                      background: 'linear-gradient(90deg, rgba(255, 122, 0, 0.55), rgba(255, 122, 0, 0.7))',
                      boxShadow: '0 0 6px rgba(255, 122, 0, 0.25)'
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* First-time tooltip */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            className="absolute -top-14 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg backdrop-blur-md border"
            style={{
              background: 'rgba(255, 72, 0, 0.95)',
              borderColor: 'rgba(255, 122, 0, 0.5)',
              boxShadow: '0 4px 20px rgba(255, 72, 0, 0.4)'
            }}
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            <div className="flex items-center gap-2 text-white text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              <span>You unlocked permanent storage!</span>
            </div>
            {/* Arrow */}
            <div
              className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2 w-3 h-3 rotate-45"
              style={{ background: 'rgba(255, 72, 0, 0.95)' }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PersonalStorageCard;
